import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, Users, FolderOpen, MapPin, Flag, X } from 'lucide-react';

const API_URL = '';

const PRIORITY_COLORS = {
  'Low': 'bg-slate-500',
  'Medium': 'bg-blue-500',
  'High': 'bg-orange-500',
  'Urgent': 'bg-red-500'
};

const PRIORITY_BORDER = {
  'Low': 'border-l-slate-500',
  'Medium': 'border-l-blue-500',
  'High': 'border-l-orange-500',
  'Urgent': 'border-l-red-500'
};

const PRIORITY_BG = {
  'Low': 'bg-slate-500/10',
  'Medium': 'bg-blue-500/10',
  'High': 'bg-orange-500/10',
  'Urgent': 'bg-red-500/10'
};

const formatTime12hr = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
};

const toLocalDateStr = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function WeeklyCalendarWidget({ user, onNavigate }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeptFilter, setSelectedDeptFilter] = useState('all');
  const scrollRef = useRef(null);
  const [previewTask, setPreviewTask] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const start = new Date(today);
    start.setDate(today.getDate() - dayOfWeek);
    start.setHours(0, 0, 0, 0);
    return start;
  });

  const isAdmin = ['admin', 'principal', 'hr'].includes(user?.role);

  const getDepartmentFilter = useCallback(() => {
    if (isAdmin) {
      if (selectedDeptFilter === 'all') return null;
      return selectedDeptFilter;
    }
    const userDept = user?.department;
    if (userDept === 'Design' || userDept === 'Construction') return userDept;
    return null;
  }, [user, isAdmin, selectedDeptFilter]);

  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      const startDate = toLocalDateStr(currentWeekStart);
      const endDate = new Date(currentWeekStart);
      endDate.setDate(endDate.getDate() + 6);
      const endDateStr = toLocalDateStr(endDate);

      const deptFilter = getDepartmentFilter();
      let url = `${API_URL}/api/calendar/tasks?start_date=${startDate}&end_date=${endDateStr}`;
      if (deptFilter) url += `&department=${deptFilter}`;

      const res = await axios.get(url);
      setTasks(res.data);
    } catch (err) {
      console.error('Failed to load calendar tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [currentWeekStart, getDepartmentFilter]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  // Auto-scroll to today on load
  useEffect(() => {
    if (!loading && scrollRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const container = scrollRef.current;
          if (!container) return;
          const today = new Date();
          const todayIdx = today.getDay();
          const todayCard = container.querySelector(`[data-testid="day-card-${todayIdx}"]`);
          if (todayCard) {
            container.scrollTo({ left: todayCard.offsetLeft - 12, behavior: 'instant' });
          }
        });
      });
    }
  }, [loading]);

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const getTasksForDate = (date) => {
    const dateStr = toLocalDateStr(date);
    return tasks.filter(t => t.task_date === dateStr).sort((a, b) => a.task_time.localeCompare(b.task_time));
  };

  const goToPrevWeek = () => {
    const s = new Date(currentWeekStart);
    s.setDate(s.getDate() - 7);
    setCurrentWeekStart(s);
  };

  const goToNextWeek = () => {
    const s = new Date(currentWeekStart);
    s.setDate(s.getDate() + 7);
    setCurrentWeekStart(s);
  };

  const goToThisWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const start = new Date(today);
    start.setDate(today.getDate() - dayOfWeek);
    start.setHours(0, 0, 0, 0);
    setCurrentWeekStart(start);
  };

  const isToday = (date) => new Date().toDateString() === date.toDateString();

  const formatWeekRange = () => {
    const endDate = new Date(currentWeekStart);
    endDate.setDate(endDate.getDate() + 6);
    const startStr = currentWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  };

  const deptFilter = getDepartmentFilter();
  const getDeptLabel = () => {
    if (isAdmin) return selectedDeptFilter === 'all' ? 'All Departments' : `${selectedDeptFilter} Calendar`;
    return deptFilter ? `${deptFilter} Calendar` : 'All Departments';
  };

  const handleCalendarClick = (dept = null) => {
    if (!onNavigate) return;
    const targetDept = dept || deptFilter || 'Design';
    if (targetDept === 'Design') onNavigate('/design', 'calendar');
    else if (targetDept === 'Construction') onNavigate('/construction', 'calendar');
  };

  const weekDays = getWeekDays();

  return (
    <Card className="glass-effect border-border/40" data-testid="weekly-calendar-widget">
      <CardHeader className="pb-2 px-3 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-primary" />
            <CardTitle className="text-base">{getDeptLabel()}</CardTitle>
            {isAdmin && (
              <select
                value={selectedDeptFilter}
                onChange={(e) => setSelectedDeptFilter(e.target.value)}
                className="text-xs px-2 py-1 rounded border border-border bg-background/50 ml-2"
                data-testid="calendar-dept-filter"
              >
                <option value="all">All</option>
                <option value="Design">Design</option>
                <option value="Construction">Construction</option>
              </select>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={goToThisWeek} className="text-xs h-7 px-2">Today</Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={goToPrevWeek}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs text-muted-foreground min-w-[120px] text-center">{formatWeekRange()}</span>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={goToNextWeek}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-0 sm:px-2 pb-2">
        {loading ? (
          <div className="text-center py-8 text-muted-foreground text-sm">Loading...</div>
        ) : (
          <div
            ref={scrollRef}
            data-testid="calendar-scroll-container"
            className="flex gap-2 sm:gap-3 overflow-x-auto px-3 sm:px-2 pb-2 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'thin' }}
          >
            {weekDays.map((date, idx) => {
              const dayTasks = getTasksForDate(date);
              const today = isToday(date);

              return (
                <div
                  key={idx}
                  className={`flex-shrink-0 snap-start rounded-lg border transition-all
                    w-[85vw] sm:w-[calc(25%-0.5rem)]
                    ${today
                      ? 'border-primary/50 bg-primary/5'
                      : 'border-border/40 bg-card/40'
                    }`}
                  data-testid={`day-card-${idx}`}
                >
                  {/* Day header */}
                  <div
                    className={`flex items-center justify-between px-3 py-2 rounded-t-lg ${
                      today ? 'bg-primary/15' : 'bg-muted/30'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`text-2xl font-bold ${today ? 'text-primary' : 'text-foreground'}`}>
                        {date.getDate()}
                      </span>
                      <div className="flex flex-col">
                        <span className={`text-xs font-medium leading-tight ${today ? 'text-primary' : 'text-foreground'}`}>
                          {DAY_NAMES[idx]}
                        </span>
                        <span className="text-[10px] text-muted-foreground leading-tight">
                          {date.toLocaleDateString('en-US', { month: 'short' })}
                        </span>
                      </div>
                    </div>
                    {today && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-primary bg-primary/20 px-1.5 py-0.5 rounded">
                        Today
                      </span>
                    )}
                    {dayTasks.length > 0 && (
                      <span className="text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                        {dayTasks.length} task{dayTasks.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  {/* Task list */}
                  <div className="p-2 space-y-1.5 min-h-[180px] sm:min-h-[200px] max-h-[60vh] sm:max-h-[280px] overflow-y-auto">
                    {dayTasks.length > 0 ? dayTasks.map(task => (
                      <div
                        key={task.id}
                        className={`p-2 rounded-md border-l-[3px] cursor-pointer hover:bg-muted/30 transition-colors ${PRIORITY_BORDER[task.priority]} ${PRIORITY_BG[task.priority]}`}
                        onClick={() => setPreviewTask(task)}
                        data-testid={`task-${task.id}`}
                      >
                        {/* Time + Priority */}
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3 h-3 text-muted-foreground" />
                            <span className="text-xs font-semibold text-foreground">{formatTime12hr(task.task_time)}</span>
                          </div>
                          <span className={`w-2 h-2 rounded-full ${PRIORITY_COLORS[task.priority]}`} title={task.priority} />
                        </div>

                        {/* Task name */}
                        <p className="text-sm font-medium text-foreground leading-snug">{task.task_name}</p>

                        {/* Description */}
                        {task.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">{task.description}</p>
                        )}

                        {/* Meta row: project + department + assignees */}
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          {task.project_name && (
                            <span className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/40 px-1.5 py-0.5 rounded">
                              <FolderOpen className="w-2.5 h-2.5" />
                              {task.project_name}
                            </span>
                          )}
                          {task.department && !deptFilter && (
                            <span className="text-[10px] text-muted-foreground bg-muted/40 px-1.5 py-0.5 rounded">
                              {task.department}
                            </span>
                          )}
                          {task.assigned_employees?.length > 0 && (
                            <span className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/40 px-1.5 py-0.5 rounded">
                              <Users className="w-2.5 h-2.5" />
                              {task.assigned_employees.length}
                            </span>
                          )}
                        </div>
                      </div>
                    )) : (
                      <div className="flex flex-col items-center justify-center h-full py-8 text-muted-foreground/40">
                        <CalendarIcon className="w-6 h-6 mb-1" />
                        <span className="text-xs">No tasks</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* View Full Calendar link */}
        <div className="mt-1 text-center">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => handleCalendarClick()}
            data-testid="btn-view-full-calendar"
          >
            View Full Calendar
          </Button>
        </div>
      </CardContent>

      {/* Task Preview Dialog */}
      <Dialog open={!!previewTask} onOpenChange={() => setPreviewTask(null)}>
        <DialogContent className="max-w-sm sm:max-w-md max-h-[80vh] overflow-y-auto" data-testid="task-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-base pr-6">{previewTask?.task_name}</DialogTitle>
          </DialogHeader>
          {previewTask && (
            <div className="space-y-3 pt-1">
              {/* Priority + Department */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white ${PRIORITY_COLORS[previewTask.priority]}`}>
                  <Flag className="w-3 h-3" /> {previewTask.priority}
                </span>
                {previewTask.department && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{previewTask.department}</span>
                )}
              </div>

              {/* Date & Time */}
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <CalendarIcon className="w-3.5 h-3.5" />
                  <span>{new Date(previewTask.task_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
                {previewTask.task_time && (
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{formatTime12hr(previewTask.task_time)}</span>
                  </div>
                )}
              </div>

              {/* Project */}
              {previewTask.project_name && (
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <FolderOpen className="w-3.5 h-3.5" />
                  <span>{previewTask.project_name}</span>
                </div>
              )}

              {/* Description */}
              {previewTask.description && (
                <div className="pt-1 border-t border-border/40">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Description</p>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{previewTask.description}</p>
                </div>
              )}

              {/* Assigned Employees */}
              {previewTask.assigned_employees?.length > 0 && (
                <div className="pt-1 border-t border-border/40">
                  <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
                    <Users className="w-3 h-3" /> Assigned ({previewTask.assigned_employees.length})
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {previewTask.assigned_employees.map((emp, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                        {emp.full_name || emp}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
